/**
 * Buffer de logs do check-in no dispositivo.
 * - console (ao vivo)
 * - sessionStorage (sobrevive refresh na mesma aba)
 * - flush HTTP para o backend (ver depois no servidor / painel)
 */

export type CheckInLogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface CheckInLogEntry {
  ts: string;
  level: CheckInLogLevel;
  event: string;
  message: string;
  data?: Record<string, unknown>;
  durationMs?: number;
}

const STORAGE_KEY = 'checkin_client_logs_v1';
const SESSION_KEY = 'checkin_client_log_session';
const MAX_LOCAL = 400;
const FLUSH_BATCH = 40;
const FLUSH_INTERVAL_MS = 12_000;

type FlushFn = (payload: {
  sessionId: string;
  eventId?: string;
  entries: CheckInLogEntry[];
  device: Record<string, unknown>;
}) => Promise<void>;

let flushFn: FlushFn | null = null;
let eventIdCtx: string | undefined;
let pending: CheckInLogEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let flushing = false;
let listeners = new Set<() => void>();

function nowIso() {
  return new Date().toISOString();
}

function loadSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return `s_${Date.now()}`;
  }
}

const sessionId = typeof window !== 'undefined' ? loadSessionId() : 'ssr';

function readStored(): CheckInLogEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CheckInLogEntry[]) : [];
  } catch {
    return [];
  }
}

function writeStored(entries: CheckInLogEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_LOCAL)));
  } catch {
    /* quota / private mode */
  }
}

function deviceInfo(): Record<string, unknown> {
  if (typeof navigator === 'undefined') return {};
  return {
    ua: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    online: navigator.onLine,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
    viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : undefined,
    secureContext: typeof window !== 'undefined' ? window.isSecureContext : undefined,
    connection: (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
      ?.effectiveType,
  };
}

/** Reduz código sensível nos logs (mostra prefixo do UUID). */
export function redactCode(code?: string | null): string | undefined {
  if (!code) return undefined;
  const c = String(code).trim();
  if (c.length <= 12) return c;
  return `${c.slice(0, 8)}…${c.slice(-4)} (${c.length} chars)`;
}

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function persistAll(entry: CheckInLogEntry) {
  const all = readStored();
  all.push(entry);
  writeStored(all);
  pending.push(entry);
  notify();
}

export function subscribeCheckInLogs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getCheckInLogSessionId(): string {
  return sessionId;
}

export function getCheckInLogs(): CheckInLogEntry[] {
  return readStored();
}

export function clearCheckInLogsLocal(): void {
  pending = [];
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

export function exportCheckInLogsText(): string {
  const lines = [
    `# check-in client logs`,
    `# session=${sessionId}`,
    `# eventId=${eventIdCtx || '-'}`,
    `# exportedAt=${nowIso()}`,
    `# device=${JSON.stringify(deviceInfo())}`,
    '',
  ];
  for (const e of readStored()) {
    const extra = e.data ? ` ${JSON.stringify(e.data)}` : '';
    const dur = e.durationMs != null ? ` (${e.durationMs}ms)` : '';
    lines.push(`${e.ts} [${e.level}] ${e.event}: ${e.message}${dur}${extra}`);
  }
  return lines.join('\n');
}

export function configureCheckInLogger(opts: {
  eventId?: string;
  flush?: FlushFn;
}): void {
  eventIdCtx = opts.eventId || eventIdCtx;
  if (opts.flush) flushFn = opts.flush;
  ensureFlushLoop();
}

export function checkInLog(
  level: CheckInLogLevel,
  event: string,
  message: string,
  data?: Record<string, unknown>,
  durationMs?: number
): void {
  const entry: CheckInLogEntry = {
    ts: nowIso(),
    level,
    event,
    message,
    ...(data && Object.keys(data).length ? { data } : {}),
    ...(durationMs != null ? { durationMs } : {}),
  };

  const prefix = `[checkin-log][${level}] ${event}`;
  if (level === 'error') console.error(prefix, message, data || '');
  else if (level === 'warn') console.warn(prefix, message, data || '');
  else console.log(prefix, message, data || '');

  persistAll(entry);

  if (level === 'error' || pending.length >= FLUSH_BATCH) {
    void flushCheckInLogs();
  }
}

export async function flushCheckInLogs(): Promise<{ sent: number; ok: boolean }> {
  if (!flushFn || flushing || pending.length === 0) {
    return { sent: 0, ok: true };
  }
  flushing = true;
  const batch = pending.splice(0, FLUSH_BATCH);
  try {
    await flushFn({
      sessionId,
      eventId: eventIdCtx,
      entries: batch,
      device: deviceInfo(),
    });
    return { sent: batch.length, ok: true };
  } catch (err) {
    // devolve pra tentar de novo
    pending = batch.concat(pending).slice(-MAX_LOCAL);
    console.warn('[checkin-log] flush failed', err);
    return { sent: 0, ok: false };
  } finally {
    flushing = false;
  }
}

function ensureFlushLoop() {
  if (typeof window === 'undefined') return;
  if (flushTimer) return;
  flushTimer = setInterval(() => {
    void flushCheckInLogs();
  }, FLUSH_INTERVAL_MS);

  const onHide = () => {
    void flushCheckInLogs();
  };
  window.addEventListener('pagehide', onHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') onHide();
  });
}

export function startTimedCheckInLog(event: string, message: string, data?: Record<string, unknown>) {
  const t0 = performance.now();
  checkInLog('debug', event, message, data);
  return {
    end(level: CheckInLogLevel, endMessage: string, endData?: Record<string, unknown>) {
      checkInLog(level, event, endMessage, endData, Math.round(performance.now() - t0));
    },
  };
}
